import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { participantsService, nationsService, ageGroupsService, pdfExportService } from "../../services";
import { useLocation } from "react-router-dom";
import AdminModalTailwind from "./AdminModalTailwind";
import ParticipantFormTailwind from "./ParticipantFormTailwind";
import ConfirmationModal from "./ConfirmationModal";
import { getAgeGroupColors } from "../../utils/ageGroupColors";

function ParticipantsTailwind() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [selectedNationality, setSelectedNationality] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [participants, setParticipants] = useState([]);
  const [nations, setNations] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [teamMembersSet, setTeamMembersSet] = useState(new Set()); // Set von Participant IDs, die im Team sind
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const participantFormRef = useRef();
  
  // Confirmation Modal States
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  
  // Groups Modal States
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [selectedParticipantGroups, setSelectedParticipantGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  
  // Export States
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  const location = useLocation();

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  // Setze Nationenfilter aus Query-Parameter beim ersten Laden
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nationParam = params.get("nation");
    if (nationParam) {
      setSelectedNationality(nationParam);
    }
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Teilnehmer, Nationen und Altersgruppen parallel
      const [participantsResponse, nationsResponse, ageGroupsResponse] = await Promise.all([
        participantsService.getAllParticipants(),
        nationsService.getAllNations(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (participantsResponse.success) {
        setParticipants(participantsResponse.data);
      } else {
        setError(participantsResponse.error);
        addToast('error', participantsResponse.error);
      }

      if (nationsResponse.success) {
        setNations(nationsResponse.data);
      } else {
        setError(nationsResponse.error);
        addToast('error', nationsResponse.error);
      }

      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
      } else {
        setError(ageGroupsResponse.error);
        addToast('error', ageGroupsResponse.error);
      }

      // Lade Team-Mitglieder für alle Nationen mit aktivierter Team-Teilnahme
      await loadTeamMembers();
    } catch (err) {
      const errorMessage = "Fehler beim Laden der Daten";
      setError(errorMessage);
      addToast('error', errorMessage);
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  // Lade Team-Mitglieder für alle Nationen
  const loadTeamMembers = async () => {
    try {
      // Hole alle Nationen mit aktivierter Team-Teilnahme
      const allNations = await nationsService.getAllNations();
      if (!allNations.success) return;

      const nationsWithTeam = allNations.data.filter(n => n.participatesInTeamCompetition || n.participates_in_team_competition);
      
      // Lade Teams für alle Nationen parallel
      const teamPromises = nationsWithTeam.map(nation => 
        nationsService.getNationTeam(nation.id).catch(() => ({ success: false, data: null }))
      );
      
      const teamResults = await Promise.all(teamPromises);
      
      // Erstelle Set von Participant IDs, die im Team sind
      const teamMemberIds = new Set();
      teamResults.forEach(result => {
        if (result.success && result.data?.teamMembers) {
          result.data.teamMembers.forEach(tm => {
            if (tm.participant?.id) {
              teamMemberIds.add(tm.participant.id);
            }
          });
        }
      });
      
      setTeamMembersSet(teamMemberIds);
    } catch (err) {
      console.error("Fehler beim Laden der Team-Mitglieder:", err);
      // Fehler beim Laden der Teams ist nicht kritisch, setze leeres Set
      setTeamMembersSet(new Set());
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleNationalityChange = (nationality) => {
    setSelectedNationality(nationality);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  // Modal und Form Handler
  const handleAddParticipant = () => {
    setEditingParticipant(null);
    setShowModal(true);
  };

  const handleEditParticipant = (participant) => {
    setEditingParticipant(participant);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingParticipant(null);
  };

  const handleSaveParticipant = async (formData) => {
    setIsSaving(true);
    try {
      let response;
      
      if (editingParticipant) {
        // Teilnehmer bearbeiten
        response = await participantsService.updateParticipant(editingParticipant.id, formData);
      } else {
        // Neuen Teilnehmer hinzufügen
        response = await participantsService.addParticipant(formData);
      }

      if (response.success) {
        // Daten neu laden
        await loadData();
        handleCloseModal();
        addToast('success', response.message || `Teilnehmer "${formData.name}" erfolgreich ${editingParticipant ? 'aktualisiert' : 'hinzugefügt'}`);
      } else {
        addToast('error', response.error || 'Fehler beim Speichern des Teilnehmers');
      }
    } catch (err) {
      addToast('error', 'Fehler beim Speichern des Teilnehmers');
      console.error("Fehler beim Speichern:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteParticipant = (participant) => {
    setParticipantToDelete(participant);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!participantToDelete) return;

    try {
      const response = await participantsService.deleteParticipant(participantToDelete.id);
      
      if (response.success) {
        await loadData();
        addToast('success', `Teilnehmer "${participantToDelete.name}" erfolgreich gelöscht`);
      } else {
        addToast('error', response.error || 'Fehler beim Löschen des Teilnehmers');
      }
    } catch (err) {
      addToast('error', 'Fehler beim Löschen des Teilnehmers');
      console.error("Fehler beim Löschen:", err);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setParticipantToDelete(null);
  };

  // Groups Modal Handler
  const handleViewGroups = async (participant) => {
    setGroupsLoading(true);
    setShowGroupsModal(true);
    
    try {
      // Lade Gruppen für den Teilnehmer
      const response = await participantsService.getParticipantGroups(participant.id);
      
      if (response.success) {
        setSelectedParticipantGroups(response.data || []);
      } else {
        setSelectedParticipantGroups([]);
        addToast('error', response.error || 'Fehler beim Laden der Gruppen');
      }
    } catch (error) {
      setSelectedParticipantGroups([]);
      addToast('error', 'Fehler beim Laden der Gruppen');
      console.error("Fehler beim Laden der Gruppen:", error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleCloseGroupsModal = () => {
    setShowGroupsModal(false);
    setSelectedParticipantGroups([]);
  };

  // Toast Handler
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      // Erstelle Filter-Objekt basierend auf aktuellen Filtern
      const filters = {
        status: selectedFilter,
        ageGroup: selectedAgeGroup,
        nationality: selectedNationality,
        gender: selectedGender,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportParticipantsPDF(filters);
      
      if (result.success) {
        addToast('success', 'PDF erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim PDF-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim PDF-Export');
      console.error("Fehler beim PDF-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      // Erstelle Filter-Objekt basierend auf aktuellen Filtern
      const filters = {
        status: selectedFilter,
        ageGroup: selectedAgeGroup,
        nationality: selectedNationality,
        gender: selectedGender,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportParticipantsJSON(filters);
      
      if (result.success) {
        addToast('success', 'JSON erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim JSON-Export');
      console.error("Fehler beim JSON-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import Handler
  const handleImportJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      addToast('error', 'Bitte wählen Sie eine JSON-Datei aus');
      return;
    }

    setIsImporting(true);
    try {
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      // Unterstütze beide Formate: direktes Array oder { data: [...] } oder { participants: [...] }
      let participantsArray = [];
      if (Array.isArray(jsonData)) {
        participantsArray = jsonData;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        participantsArray = jsonData.data;
      } else if (jsonData.participants && Array.isArray(jsonData.participants)) {
        participantsArray = jsonData.participants;
      } else {
        throw new Error('Ungültiges JSON-Format. Erwartet wird ein Array oder ein Objekt mit "data" oder "participants"');
      }

      const result = await pdfExportService.importParticipantsJSON(participantsArray);
      
      if (result.success) {
        let message = result.message || 'JSON erfolgreich importiert';
        if (result.data) {
          message = `${message} (${result.data.created || 0} erstellt, ${result.data.updated || 0} aktualisiert${result.data.errors > 0 ? `, ${result.data.errors} Fehler` : ''})`;
        }
        addToast('success', message);
        // Daten neu laden
        await loadData();
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Import');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Importieren der JSON-Datei: ' + error.message);
      console.error("Fehler beim JSON-Import:", error);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const filterOptions = [
    { value: "all", label: t("admin.participants.filters.all", "Alle") },
    { value: "confirmed", label: t("admin.participants.filters.confirmed", "Bestätigt") },
    { value: "pending", label: t("admin.participants.filters.pending", "Ausstehend") }
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.participants.ageGroups.all", "Alle Altersgruppen") },
    ...ageGroups
      .filter((ageGroup) => ageGroup.name !== 'Alle') // "Alle" ausschließen
      .map((ageGroup) => ({
        value: ageGroup.name,
        label: ageGroup.name
      }))
  ];

  const genderOptions = [
    { value: "all", label: t("admin.participants.gender.all", "Alle Geschlechter") },
    { value: "männlich", label: t("admin.participants.gender.male", "Männlich") },
    { value: "weiblich", label: t("admin.participants.gender.female", "Weiblich") }
  ];

  // Filter- und Suchlogik
  const filteredParticipants = participants.filter((participant) => {
    // Suche nach Name (case-insensitive)
    const matchesSearch = participant.name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status-Filter
    const matchesStatus =
      selectedFilter === "all" ||
      (selectedFilter === "confirmed" && participant.status?.toLowerCase() === "bestätigt") ||
      (selectedFilter === "pending" && participant.status?.toLowerCase() === "ausstehend");

    // Altersgruppen-Filter
    const matchesAgeGroup =
      selectedAgeGroup === "all" || (participant.ageGroup?.name || participant.ageGroup) === selectedAgeGroup;

    // Nationalitäten-Filter
    const matchesNationality =
      selectedNationality === "all" || (participant.nation?.code || participant.nationCode) === selectedNationality;

    // Geschlechts-Filter
    const matchesGender =
      selectedGender === "all" || participant.gender === selectedGender;

    return matchesSearch && matchesStatus && matchesAgeGroup && matchesNationality && matchesGender;
  });

  // Schließe Export-Menü beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.relative')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="flex gap-2">
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="space-y-6">
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-3"></div>
                <div className="flex flex-wrap gap-1.5">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(9)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(9)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadData} 
                  className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.participants.title", "Teilnehmerverwaltung")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.participants.subtitle", "Verwalte alle Teilnehmer der Veranstaltung.")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredParticipants.length}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Teilnehmer</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Controls Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t("admin.participants.search", "Teilnehmer suchen...")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
            </div>

            {/* Actions */}
            <div className="flex gap-2 items-stretch">
              <button 
                onClick={handleAddParticipant}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 h-[46px]"
              >
                <i className="fas fa-plus text-xs"></i>
                {t("admin.participants.add", "Hinzufügen")}
              </button>
              
              <div className="relative">
                <button 
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={isExporting}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed h-[46px]"
                >
                  <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} text-xs`}></i>
                  {isExporting 
                    ? t("admin.participants.exporting", "Export...") 
                    : t("admin.participants.export", "Export")
                  }
                  {!isExporting && <i className="fas fa-chevron-down text-xs"></i>}
                </button>
                
                {showExportMenu && !isExporting && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 z-10">
                    <button
                      onClick={handleExportPDF}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                    >
                      <i className="fas fa-file-pdf text-red-600 text-xs"></i>
                      <span>Als PDF exportieren</span>
                    </button>
                    <button
                      onClick={handleExportJSON}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                    >
                      <i className="fas fa-file-code text-blue-600 text-xs"></i>
                      <span>Als JSON exportieren</span>
                    </button>
                  </div>
                )}
              </div>

              <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed h-[46px]">
                <i className={`fas ${isImporting ? 'fa-spinner fa-spin' : 'fa-upload'} text-xs`}></i>
                {isImporting ? "Import..." : "Import"}
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportJSON}
                  disabled={isImporting}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Filter Groups */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="space-y-6">
            {/* Status Filter */}
            <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.participants.filters.title", "Status")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleFilterChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedFilter === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.participants.ageGroups.title", "Altersgruppe")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {ageGroupOptions.map((option) => {
                    const isSelected = selectedAgeGroup === option.value;
                    
                    if (option.value === "all") {
                      return (
                        <button
                          key={option.value}
                          onClick={() => handleAgeGroupChange(option.value)}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            isSelected
                              ? 'bg-primary dark:bg-primary-light text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    }
                    
                    const colors = getAgeGroupColors(option.value);
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleAgeGroupChange(option.value)}
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                          isSelected
                            ? `${colors.bg} text-white`
                            : `${colors.bgLight} ${colors.text} hover:${colors.bg} hover:text-white`
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.participants.gender.title", "Geschlecht")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {genderOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleGenderChange(option.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedGender === option.value
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nationality Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.participants.nationality.title", "Nationalität")}
                </h3>
                <div className="max-h-64 overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => handleNationalityChange("all")}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedNationality === "all"
                          ? 'bg-primary dark:bg-primary-light text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      <span role="img" aria-label="Alle Nationen" className="mr-1">🌐</span>
                      {t("admin.participants.nationality.all", "Alle")}
                    </button>
                    {nations.map((nation) => (
                      <button
                        key={nation.id}
                        onClick={() => handleNationalityChange(nation.code)}
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors gap-1.5 ${
                          selectedNationality === nation.code
                            ? 'bg-primary dark:bg-primary-light text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {nation.code && (
                          <img
                            src={process.env.PUBLIC_URL + "/images/flags/" + nation.code + ".png"}
                            alt={nation.name}
                            className="w-4 h-4 object-cover rounded-full"
                          />
                        )}
                        {nation.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Table */}
        {filteredParticipants.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.id", "ID")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.name", "Name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.nation", "Nation")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.age", "Alter")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.gender", "Geschlecht")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.ageGroup", "Altersgruppe")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-64">
                    {t("admin.participants.table.category", "Kategorien")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.status", "Status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.actions", "Aktionen")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredParticipants.map((participant) => (
                  <tr key={participant.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                      {participant.participantNumber || participant.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {participant.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + (participant.nation?.code || participant.nationCode) + ".png"}
                          alt={participant.nation?.name || participant.nation}
                          className="w-6 h-6 object-cover rounded-full border border-slate-200"
                        />
                        <span className="text-sm text-slate-900 dark:text-slate-100">
                          {participant.nation?.name || participant.nation}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                      {participant.age}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        participant.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                        participant.gender === 'weiblich' ? 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' :
                        'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}>
                        {participant.gender === 'männlich' ? 'Männlich' :
                         participant.gender === 'weiblich' ? 'Weiblich' : participant.gender}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {participant.ageGroup?.name || participant.ageGroup ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          (() => {
                            const colors = getAgeGroupColors(participant.ageGroup?.name || participant.ageGroup);
                            // Map bgLight to dark mode equivalents
                            const darkBgMap = {
                              'bg-emerald-100': 'dark:bg-emerald-900/30',
                              'bg-blue-100': 'dark:bg-blue-900/30',
                              'bg-violet-100': 'dark:bg-violet-900/30',
                              'bg-amber-100': 'dark:bg-amber-900/30',
                              'bg-red-100': 'dark:bg-red-900/30',
                              'bg-gray-100': 'dark:bg-gray-700'
                            };
                            // Map text to dark mode equivalents
                            const darkTextMap = {
                              'text-emerald-700': 'dark:text-emerald-300',
                              'text-blue-700': 'dark:text-blue-300',
                              'text-violet-700': 'dark:text-violet-300',
                              'text-amber-700': 'dark:text-amber-300',
                              'text-red-700': 'dark:text-red-300',
                              'text-gray-800': 'dark:text-gray-300'
                            };
                            return `${colors.bgLight} ${darkBgMap[colors.bgLight] || 'dark:bg-slate-700'} ${colors.text} ${darkTextMap[colors.text] || 'dark:text-slate-300'}`;
                          })()
                        }`}>
                          {participant.ageGroup?.name || participant.ageGroup}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 w-64">
                      <div className="space-y-1">
                        {(participant.categories || participant.category || []).map((cat, index) => (
                          <div
                            key={index}
                            className="inline-flex px-2 py-1 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md w-full"
                          >
                            {cat.type === 'Offene Form' && (cat.formName || '').trim()
                              ? `${cat.name} – ${cat.formName}`
                              : (cat.name || cat)
                            }
                          </div>
                        ))}
                        {teamMembersSet.has(participant.id) && (
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded-md w-full shadow-sm border border-blue-600" title="Im Team der Nation für den Gruppenwettbewerb">
                            <i className="fas fa-users text-[10px]"></i>
                            <span>Gruppenwettbewerb</span>
                          </div>
                        )}
                        {(participant.categories || participant.category || []).length === 0 && !teamMembersSet.has(participant.id) && (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        participant.status?.toLowerCase() === 'bestätigt' 
                          ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {participant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => handleEditParticipant(participant)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                          title={t("admin.participants.actions.edit", "Bearbeiten")}
                        >
                          <i className="fas fa-edit text-xs"></i>
                        </button>
                        <button 
                          onClick={() => handleViewGroups(participant)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                          title={t("admin.participants.actions.viewGroups", "Gruppen ansehen")}
                        >
                          <i className="fas fa-users text-xs"></i>
                        </button>
                        <button 
                          onClick={() => handleDeleteParticipant(participant)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={t("admin.participants.actions.delete", "Löschen")}
                        >
                          <i className="fas fa-trash text-xs"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredParticipants.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-users text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Teilnehmer gefunden</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {searchTerm || selectedFilter !== "all" || selectedAgeGroup !== "all" || selectedNationality !== "all" || selectedGender !== "all"
                ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                : "Fügen Sie den ersten Teilnehmer hinzu, um zu beginnen."
              }
            </p>
            {(!searchTerm && selectedFilter === "all" && selectedAgeGroup === "all" && selectedNationality === "all" && selectedGender === "all") && (
              <button
                onClick={handleAddParticipant}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-md hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <i className="fas fa-plus text-xs"></i>
                Ersten Teilnehmer hinzufügen
              </button>
            )}
          </div>
        )}

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white ${
            toast.type === 'success' ? 'border-green-200' :
            toast.type === 'error' ? 'border-red-200' :
            'border-blue-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                toast.type === 'success' ? 'bg-green-100' :
                toast.type === 'error' ? 'bg-red-100' :
                'bg-blue-100'
              }`}>
                <i className={`fas text-xs ${
                  toast.type === 'success' ? 'fa-check text-green-600' :
                  toast.type === 'error' ? 'fa-times text-red-600' :
                  'fa-info text-blue-600'
                }`}></i>
              </div>
              <span className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-900' :
                toast.type === 'error' ? 'text-red-900' :
                'text-blue-900'
              }`}>{toast.message}</span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal für Teilnehmer hinzufügen/bearbeiten */}
      <AdminModalTailwind
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingParticipant 
          ? t("admin.participants.editTitle", "Teilnehmer bearbeiten") 
          : t("admin.participants.addTitle", "Neuen Teilnehmer hinzufügen")
        }
        subtitle={editingParticipant 
          ? t("admin.participants.editSubtitle", "Bearbeiten Sie die Daten des Teilnehmers.") 
          : t("admin.participants.addSubtitle", "Fügen Sie einen neuen Teilnehmer zur Veranstaltung hinzu.")
        }
        onSave={() => participantFormRef.current?.handleSubmit()}
        onCancel={handleCloseModal}
        saveButtonText={editingParticipant 
          ? t("admin.participants.update", "Aktualisieren") 
          : t("admin.participants.save", "Hinzufügen")
        }
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={isSaving}
      >
        <ParticipantFormTailwind
          ref={participantFormRef}
          participant={editingParticipant}
          onSave={handleSaveParticipant}
          onCancel={handleCloseModal}
          isSaving={isSaving}
        />
      </AdminModalTailwind>

      {/* Bestätigungsmodal für Löschen */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmDelete}
        title="Teilnehmer löschen"
        subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
        message={`Möchten Sie den Teilnehmer "${participantToDelete?.name}" wirklich löschen?`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
        layout="inline"
      />

      {/* Gruppen-Modal */}
      {showGroupsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-slate-200 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Gruppen des Teilnehmers</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {selectedParticipantGroups.length} Gruppe{selectedParticipantGroups.length !== 1 ? 'n' : ''} gefunden
                  </p>
                </div>
                <button
                  onClick={handleCloseGroupsModal}
                  className="p-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] bg-white dark:bg-slate-800">
              {groupsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 dark:border-slate-400"></div>
                  <span className="ml-3 text-slate-600 dark:text-slate-400">Lade Gruppen...</span>
                </div>
              ) : selectedParticipantGroups.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-users text-slate-400 dark:text-slate-500 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Gruppen gefunden</h4>
                  <p className="text-slate-500 dark:text-slate-400">Dieser Teilnehmer ist noch keiner Gruppe zugewiesen.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedParticipantGroups.map((group) => (
                    <div key={group.id} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 p-4">
                      {/* Gruppen-Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{group.name}</h4>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {group.category?.name || group.category}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">•</span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {group.ageGroup?.name || group.ageGroup}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">•</span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {group.gender === 'männlich' ? 'Männlich' :
                               group.gender === 'weiblich' ? 'Weiblich' :
                               group.gender || 'Unbekannt'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${
                            group.status === 'Abgeschlossen' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                            group.status === 'Aktiv' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                            group.status === 'Vorbereitung' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                          }`}>
                            <i className={`fas ${
                              group.status === 'Aktiv' ? 'fa-play' : 
                              group.status === 'Ausstehend' ? 'fa-clock' : 
                              group.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                              'fa-check'
                            } mr-1.5`}></i>
                            {group.status}
                          </span>
                        </div>
                      </div>

                      {/* Gruppen-Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Teilnehmerzahl</div>
                          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            {group.participantCount || group.participants?.length || 0}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Startnummer</div>
                          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            #{group.startNumber || '-'}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Platzierung</div>
                          <div className={`text-lg font-semibold ${
                            group.rank === 1 ? 'text-yellow-700 dark:text-yellow-300' :
                            group.rank === 2 ? 'text-slate-600 dark:text-slate-400' :
                            group.rank === 3 ? 'text-orange-700 dark:text-orange-300' :
                            'text-slate-900 dark:text-slate-100'
                          }`}>
                            {group.rank ? `${group.rank}.` : '-'}
                          </div>
                        </div>
                      </div>

                      {/* Ergebnisse (falls vorhanden) */}
                      {group.results && group.results.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Ergebnisse</h5>
                          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 dark:bg-slate-700/50">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">WR 1</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">WR 2</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">WR 3</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">WR 4</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">WR 5</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">Penalty</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">Punkte</th>
                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400">Rang</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                  {group.results.map((result, index) => (
                                    <tr key={index} className={
                                      result.rank === 1 ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                                      result.rank === 2 ? 'bg-slate-50 dark:bg-slate-700/30' :
                                      result.rank === 3 ? 'bg-orange-50 dark:bg-orange-900/20' :
                                      ''
                                    }>
                                      <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{result.judgeScore1 || '-'}</td>
                                      <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{result.judgeScore2 || '-'}</td>
                                      <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{result.judgeScore3 || '-'}</td>
                                      <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{result.judgeScore4 || '-'}</td>
                                      <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{result.judgeScore5 || '-'}</td>
                                      <td className="px-3 py-2 text-slate-900 dark:text-slate-100">
                                        {result.penalty ? (result.penalty > 0 ? `+${result.penalty}` : result.penalty) : '-'}
                                      </td>
                                      <td className="px-3 py-2 font-semibold text-slate-900 dark:text-slate-100">{result.finalScore || '-'}</td>
                                      <td className="px-3 py-2">
                                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                                          result.rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                          result.rank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' :
                                          result.rank === 3 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                                          'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                        }`}>
                                          {result.rank ? `${result.rank}.` : '-'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Zeitplan (falls vorhanden) */}
                      {group.scheduleEvent && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Zeitplan</h5>
                          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 p-3">
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1">
                                <i className="fas fa-calendar text-slate-400 dark:text-slate-500"></i>
                                <span className="text-slate-600 dark:text-slate-300">
                                  {new Date(group.scheduleEvent.startTime).toLocaleDateString('de-DE')}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="fas fa-clock text-slate-400 dark:text-slate-500"></i>
                                <span className="text-slate-600 dark:text-slate-300">
                                  {new Date(group.scheduleEvent.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - 
                                  {new Date(group.scheduleEvent.endTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="fas fa-map-marker-alt text-slate-400 dark:text-slate-500"></i>
                                <span className="text-slate-600 dark:text-slate-300">{group.scheduleEvent.venue}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
</div>
    </main>
  );
}

export default ParticipantsTailwind;
